import { CategoryAxis, LineSeries, ValueAxis, XYChart } from "@amcharts/amcharts4/charts";
import { color, create, Label } from "@amcharts/amcharts4/core";
import _ from "lodash";
import React, { Component } from "react";
import WebFont from "webfontloader";
import "../../../assets/styles/component/legend.scss";
import { BLACK, SOV_MIX_COLORS, WHITE } from "../../../constants";
import { disposeChart } from "../helpers/chartHelpers";
import { configureCategoryAxis, configureValueAxis, lineSeriesConfiguration } from "../helpers/configurations";
import { legendConfigurationOCE, oceChartConfiguration } from "../helpers/oceConfigurations";
import {
  formatChinaDateAxisLabelToQuarterlyFY,
  formatChinaDateAxisLabelToYearlyFY,
  formatDateAxisLabelQuarterToWave,
  formatDateAxisLabelToMonthlyFY,
  formatDateAxisLabelToQuarterlyFY,
  formatDateAxisLabelToYearlyFY,
} from "../helpers/pmoConfigurations";
import { ThemeInt } from "../helpers/types";

interface Props {
  data: any;
  granularity: string;
  theme: ThemeInt;
  chartId: string;
  seriesList: { name: string; field: string; hide: boolean; colour?: string }[];
  percentageChart?: boolean;
  nissanCompetitor?: string;
  market: string;
  diff: number;
  legendPosition?: "right" | "left" | "top" | "bottom";
  showSOVchartText?: boolean;
  showPTVAchartText?: boolean;
  numberFormat?: string;
  axisRangeValue?: number | null;
  noValueAxisMin?: boolean;
}

const marketsForQuarterToWaveFormatter = ["Brazil", "Thailand", "Chile", "Argentina", "Saudi Arabia", "Uae", "Australia", "Philippines"];

class BrandPerformanceTrendChart extends Component<Props> {
  chartId = this.props.chartId;
  chart!: XYChart;

  componentDidMount() {
    WebFont.load({
      custom: { families: ["nissan"], urls: ["../../../assets/fonts/fonts.css"] },
      active: () => this.initChart(),
      timeout: 8000,
    });
  }

  componentDidUpdate(prevProps: any) {
    if (!_.isEqual(prevProps, this.props)) {
      disposeChart(this.chartId);
      this.initChart();
    }
  }

  componentWillUnmount() {
    disposeChart(this.chartId);
  }

  createSOVSOMTextLabel = (chart: XYChart, theme: ThemeInt, text: string) => {
    const label = chart.plotContainer.createChild(Label);
    label.fontSize = 12;
    label.text = text;
    label.fill = theme === "dark" ? color(WHITE) : color(BLACK);
    label.align = "left";
    label.isMeasured = false;
    label.x = 0;
    label.y = -20;
  };

  initChart() {
    const {
      data,
      theme,
      seriesList,
      percentageChart,
      market,
      legendPosition = "top",
      showSOVchartText = false,
      showPTVAchartText = false,
      numberFormat = percentageChart || showPTVAchartText ? "#,###.0" : "#,###",
      axisRangeValue,
      noValueAxisMin,
      granularity,
    } = this.props;

    this.chart = create(this.chartId, XYChart);
    oceChartConfiguration(this.chart);

    // Format data for CategoryAxis (quarters/waves) or keep original dates for monthly/yearly
    const chartData = data.map((d: any) => {
      let label = d.date;
      if (granularity === "monthly") {
        label = formatDateAxisLabelToMonthlyFY(d.date);
      } else if (granularity === "quarterly") {
        label =
          market === "China" || market === "China_Ev"
            ? formatChinaDateAxisLabelToQuarterlyFY(d.date)
            : marketsForQuarterToWaveFormatter.includes(market)
            ? formatDateAxisLabelQuarterToWave(d.date)
            : formatDateAxisLabelToQuarterlyFY(d.date);
      } else if (granularity === "yearly") {
        label =
          market === "China" || market === "China_Ev" ? formatChinaDateAxisLabelToYearlyFY(d.date) : formatDateAxisLabelToYearlyFY(d.date);
      }
      return { ...d, label };
    });
    this.chart.data = chartData;

    // Legend
    this.chart.legend = legendConfigurationOCE(theme);
    this.chart.colors.list = SOV_MIX_COLORS.map((c) => color(c));
    this.chart.legend.marginBottom = 30;
    this.chart.legend.maxHeight = 100;
    this.chart.legend.scrollable = true;
    this.chart.legend.position = legendPosition;
    this.chart.legend.reverseOrder = false;

    showSOVchartText && this.createSOVSOMTextLabel(this.chart, theme, "*1.0 = 'SOV is at the same level as SOM'");
    showPTVAchartText && this.createSOVSOMTextLabel(this.chart, theme, "Nissan = 100");

    // X-axis
    const categoryAxis = this.chart.xAxes.push(new CategoryAxis());
    categoryAxis.dataFields.category = "label";
    configureCategoryAxis(categoryAxis, theme);
    categoryAxis.renderer.minGridDistance = 70;
    categoryAxis.renderer.labels.template.rotation = 0;
    categoryAxis.renderer.labels.template.wrap = true;
    categoryAxis.renderer.labels.template.horizontalCenter = "middle";
    categoryAxis.renderer.labels.template.verticalCenter = "top";

    // Y-axis
    const valueAxis = this.chart.yAxes.push(new ValueAxis());
    configureValueAxis(valueAxis, theme);
    valueAxis.extraMax = 0.1;
    if (!noValueAxisMin) valueAxis.min = 0;

    if (axisRangeValue) {
      const range = valueAxis.axisRanges.create();
      range.value = axisRangeValue;
      range.grid.strokeWidth = 3;
      range.grid.strokeOpacity = 1;
    }

    if (percentageChart) valueAxis.extraMax = 0;

    // Build a colour map keyed by base field name so that segments of the same
    // logical series that are split by a line cut / methodology break (e.g.
    // "awareness" before the break and "_awareness" after it) always share the
    // same colour. Without this, amcharts assigns palette colours by series
    // index, so the post-break segment gets the next palette colour and no
    // longer matches the pre-break segment.
    const baseFieldColour: Record<string, string> = {};
    let paletteIndex = 0;
    seriesList.forEach(({ field, colour }) => {
      const baseField = field.replace(/^_+/, "");
      if (baseFieldColour[baseField]) return;
      // Prefer an explicit colour supplied on any segment of this base field.
      const explicit = seriesList.find((s) => s.field.replace(/^_+/, "") === baseField && s.colour)?.colour;
      if (explicit) {
        baseFieldColour[baseField] = explicit;
      } else {
        baseFieldColour[baseField] = SOV_MIX_COLORS[paletteIndex % SOV_MIX_COLORS.length];
        paletteIndex += 1;
      }
    });

    // Series
    seriesList.forEach(({ name, field, hide }) => {
      const series = this.chart.series.push(new LineSeries());
      const bullet = lineSeriesConfiguration(series, { valueY: field, name, granularity });
      series.dataFields.categoryX = "label";
      series.dataFields.valueY = field;
      series.hiddenInLegend = hide ?? false;

      const seriesColour = baseFieldColour[field.replace(/^_+/, "")];
      if (seriesColour) {
        series.stroke = color(seriesColour);
        series.fill = color(seriesColour);
      }

      // Tooltip
      bullet.adapter.add("tooltipText", (text, target) => {
        const dataContext = target?.dataItem?.dataContext as any;
        const date = dataContext?.date ?? "";
        let dateVal = date;

        if (granularity === "monthly") {
          dateVal = formatDateAxisLabelToMonthlyFY(date);
        } else if (granularity === "quarterly") {
          dateVal =
            market === "China" || market === "China_Ev"
              ? formatChinaDateAxisLabelToQuarterlyFY(date)
              : marketsForQuarterToWaveFormatter.includes(market)
              ? formatDateAxisLabelQuarterToWave(date)
              : formatDateAxisLabelToQuarterlyFY(date);
        } else if (granularity === "yearly") {
          dateVal =
            market === "China" || market === "China_Ev" ? formatChinaDateAxisLabelToYearlyFY(date) : formatDateAxisLabelToYearlyFY(date);
        }

        return `[bold; font-size: var(--regular_font_size);] ${dateVal} ${name}[/ font-size: var(--regular_font_size);]: {valueY.value.formatNumber("${numberFormat}")}${
          percentageChart ? "%" : ""
        }`;
      });

      // Legend filtering
      if (hide !== undefined && !hide) {
        const baseField = field.replace(/^_+/, "");
        series.events.on("hidden", () => {
          this.chart.series.each((s) => {
            const sField = s.dataFields.valueY || "";
            if (sField.replace(/^_+/, "") === baseField && s !== series) s.hide();
          });
        });
        series.events.on("shown", () => {
          this.chart.series.each((s) => {
            const sField = s.dataFields.valueY || "";
            if (sField.replace(/^_+/, "") === baseField && s !== series) s.show();
          });
        });
      }
    });
  }

  render() {
    return (
      <div className="chartWrapper">
        <div id={this.props.chartId} data-test-id={this.props.chartId} className="graph" />
      </div>
    );
  }
}

export default BrandPerformanceTrendChart;
